<script setup>
import { useAlert } from 'dashboard/composables';
import AddAutomationRule from './AddAutomationRule.vue';
import EditAutomationRule from './EditAutomationRule.vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import AutomationRuleRow from './AutomationRuleRow.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const confirmDialog = ref(null);

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedAutomation = ref({});
const searchQuery = ref('');
const toggleModalTitle = ref(t('AUTOMATION.TOGGLE.ACTIVATION_TITLE'));
const toggleModalDescription = ref(
  t('AUTOMATION.TOGGLE.ACTIVATION_DESCRIPTION')
);

const records = computed(() => getters['automations/getAutomations'].value);
const uiFlags = computed(() => getters['automations/getUIFlags'].value);
const accountId = computed(() => getters.getCurrentAccountId.value);

// Filtered automations based on search
const filteredRecords = computed(() => {
  if (!searchQuery.value) return records.value;
  const query = searchQuery.value.toLowerCase();
  return records.value.filter(
    automation =>
      automation.name.toLowerCase().includes(query) ||
      (automation.description &&
        automation.description.toLowerCase().includes(query))
  );
});

const deleteConfirmText = computed(
  () => `${t('AUTOMATION.DELETE.CONFIRM.YES')} ${selectedAutomation.value.name}`
);

const deleteRejectText = computed(
  () => `${t('AUTOMATION.DELETE.CONFIRM.NO')} ${selectedAutomation.value.name}`
);

const deleteMessage = computed(() => ` ${selectedAutomation.value.name}?`);

const isSLAEnabled = computed(() =>
  getters['accounts/isFeatureEnabledonAccount'].value(accountId.value, 'sla')
);

onMounted(() => {
  store.dispatch('inboxes/get');
  store.dispatch('agents/get');
  store.dispatch('contacts/get');
  store.dispatch('teams/get');
  store.dispatch('labels/get');
  store.dispatch('campaigns/get');
  store.dispatch('automations/get');
  if (isSLAEnabled.value) {
    store.dispatch('sla/get');
  }
});

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  selectedAutomation.value = response;
  showEditPopup.value = true;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedAutomation.value = response;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteAutomation = async id => {
  try {
    await store.dispatch('automations/delete', id);
    useAlert(t('AUTOMATION.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AUTOMATION.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[selectedAutomation.value.id] = false;
  }
};
const confirmDeletion = () => {
  loading.value[selectedAutomation.value.id] = true;
  closeDeletePopup();
  deleteAutomation(selectedAutomation.value.id);
};
const cloneAutomation = async ({ id }) => {
  try {
    await store.dispatch('automations/clone', id);
    useAlert(t('AUTOMATION.CLONE.API.SUCCESS_MESSAGE'));
    store.dispatch('automations/get');
  } catch (error) {
    useAlert(t('AUTOMATION.CLONE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[selectedAutomation.value.id] = false;
  }
};

const submitAutomation = async (payload, mode) => {
  try {
    const action =
      mode === 'edit' ? 'automations/update' : 'automations/create';
    const successMessage =
      mode === 'edit'
        ? t('AUTOMATION.EDIT.API.SUCCESS_MESSAGE')
        : t('AUTOMATION.ADD.API.SUCCESS_MESSAGE');
    await store.dispatch(action, payload);
    useAlert(successMessage);
    hideAddPopup();
    hideEditPopup();
  } catch (error) {
    const errorMessage =
      mode === 'edit'
        ? t('AUTOMATION.EDIT.API.ERROR_MESSAGE')
        : t('AUTOMATION.ADD.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};
const toggleAutomation = async ({ id, name, status }) => {
  try {
    if (status) {
      toggleModalTitle.value = t('AUTOMATION.TOGGLE.DEACTIVATION_TITLE');
      toggleModalDescription.value = t(
        'AUTOMATION.TOGGLE.DEACTIVATION_DESCRIPTION',
        {
          automationName: name,
        }
      );
    } else {
      toggleModalTitle.value = t('AUTOMATION.TOGGLE.ACTIVATION_TITLE');
      toggleModalDescription.value = t(
        'AUTOMATION.TOGGLE.ACTIVATION_DESCRIPTION',
        {
          automationName: name,
        }
      );
    }

    const ok = await confirmDialog.value.showConfirmation();
    if (ok) {
      await store.dispatch('automations/update', {
        id: id,
        active: !status,
      });
      const message = status
        ? t('AUTOMATION.TOGGLE.DEACTIVATION_SUCCESFUL')
        : t('AUTOMATION.TOGGLE.ACTIVATION_SUCCESFUL');
      useAlert(message);
    }
  } catch (error) {
    useAlert(t('AUTOMATION.EDIT.API.ERROR_MESSAGE'));
  }
};

const tableHeaders = computed(() => {
  return [
    t('AUTOMATION.LIST.TABLE_HEADER.NAME'),
    t('AUTOMATION.LIST.TABLE_HEADER.DESCRIPTION'),
    t('AUTOMATION.LIST.TABLE_HEADER.ACTIVE'),
    t('AUTOMATION.LIST.TABLE_HEADER.CREATED_ON'),
  ];
});
</script>

<template>
  <div class="settings-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header
      class="settings-header flex items-center justify-between px-6 flex-shrink-0"
      style="height: 50px"
    >
      <h1 class="text-xl font-semibold" style="color: #111827">
        {{ $t('AUTOMATION.HEADER') }}
      </h1>
      <button class="primary-action-btn" @click="openAddPopup">
        <span class="i-ph-plus-bold size-4" />
        {{ $t('AUTOMATION.HEADER_BTN_TXT') }}
      </button>
    </header>

    <!-- Description -->
    <div class="ws-desc-bar px-6 py-3">
      <p class="text-sm" style="color: #4b5563">
        {{ $t('AUTOMATION.DESCRIPTION') }}
      </p>
    </div>

    <!-- Search and Filters -->
    <div class="ws-desc-bar px-6 py-3">
      <div class="flex items-center gap-4">
        <div class="relative flex-1 max-w-xs">
          <span
            class="absolute left-3 top-1/2 -translate-y-1/2 i-ph-magnifying-glass-bold size-4 pointer-events-none"
            style="color: #9ca3af"
          />
          <input
            v-model="searchQuery"
            type="text"
            class="search-input reset-base w-full pl-10 pr-4 py-2.5 text-sm rounded-lg"
            placeholder="Search automations..."
          />
        </div>
        <div class="text-sm self-center" style="color: #6b7280">
          {{ filteredRecords.length }} automations
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="ws-content flex-1 overflow-y-auto p-6">
      <!-- Loading State -->
      <div
        v-if="uiFlags.isFetching"
        class="flex items-center justify-center py-20"
      >
        <woot-loading-state :message="$t('AUTOMATION.LOADING')" />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!filteredRecords.length"
        class="flex items-center justify-center py-20"
      >
        <p style="color: #6b7280">{{ $t('AUTOMATION.LIST.404') }}</p>
      </div>

      <!-- Automation Table -->
      <table v-else class="w-full">
        <thead>
          <tr class="ws-thead-row">
            <th
              v-for="thHeader in tableHeaders"
              :key="thHeader"
              class="px-6 py-3"
            >
              {{ thHeader }}
            </th>
            <th class="px-6 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody class="ws-tbody">
          <AutomationRuleRow
            v-for="automation in filteredRecords"
            :key="automation.id"
            :automation="automation"
            :loading="loading[automation.id]"
            @clone="cloneAutomation"
            @toggle="toggleAutomation"
            @edit="openEditPopup"
            @delete="openDeletePopup"
          />
        </tbody>
      </table>
    </div>

    <!-- Modals -->
    <woot-modal
      v-model:show="showAddPopup"
      size="medium"
      :on-close="hideAddPopup"
    >
      <AddAutomationRule
        v-if="showAddPopup"
        :on-close="hideAddPopup"
        @save-automation="submitAutomation"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('AUTOMATION.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />

    <woot-modal
      v-model:show="showEditPopup"
      size="medium"
      :on-close="hideEditPopup"
    >
      <EditAutomationRule
        v-if="showEditPopup"
        :on-close="hideEditPopup"
        :selected-response="selectedAutomation"
        @save-automation="submitAutomation"
      />
    </woot-modal>
    <woot-confirm-modal
      ref="confirmDialog"
      :title="toggleModalTitle"
      :description="toggleModalDescription"
    />
  </div>
</template>

<style scoped>
.settings-page,
.dark .settings-page {
  background: #ffffff !important;
}

.settings-header {
  background: #ffffff !important;
  border-bottom: 1px solid #e5e7eb !important;
}

.ws-toolbar,
.ws-desc-bar {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
}

.ws-content {
  background: #ffffff;
}

.ws-search,
.search-input {
  background: #f9fafb !important;
  border: 1px solid #e5e7eb !important;
  color: #111827 !important;
}

.ws-search:focus,
.search-input:focus {
  outline: none;
  border-color: #0066ff !important;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
  background: #ffffff !important;
}

.ws-search::placeholder,
.search-input::placeholder {
  color: #9ca3af !important;
}

.primary-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Excon', system-ui, sans-serif;
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.primary-action-btn:hover {
  background: #1f2937;
}

.primary-action-btn:active {
  transform: scale(0.98);
}

.primary-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ws-thead-row th {
  padding: 10px 24px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #374151 !important;
  background: #f9fafb !important;
  border-bottom: 1px solid #e5e7eb !important;
}

.ws-tbody tr {
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.12s ease;
}

.ws-tbody tr:hover {
  background: #fafafa;
}

.ws-tbody td {
  color: #111827 !important;
  padding: 14px 24px;
  font-size: 13px;
  vertical-align: middle;
}

/* Force modal editor surfaces to white */
:deep(.modal-mask .modal-container) {
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
}

:deep(.modal-mask .modal-header),
:deep(.modal-mask .modal-content),
:deep(.modal-mask form) {
  background: #ffffff !important;
}

:deep(label) {
  color: #111827 !important;
}

:deep(input:not(.reset-base)),
:deep(select:not(.reset-base)),
:deep(textarea:not(.reset-base)) {
  background: #ffffff !important;
  border-color: #d1d5db !important;
  color: #111827 !important;
}

:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
:deep(.text-n-slate-10) {
  color: #6b7280 !important;
}
:deep(.bg-n-background),
:deep(.bg-n-solid-1),
:deep(.bg-n-solid-2) {
  background: #ffffff !important;
}
:deep(.border-n-weak),
:deep(.border-n-strong) {
  border-color: #e5e7eb !important;
}
</style>
